import { MEDIA_QUERIES } from '@lib/style/breakpoints';
import { useSyncExternalStore } from 'react';

/**
 * `hover` — not `pointer` — is deliberate. The failure this guards against is a control that can
 * only be reached by hovering, so the question is whether the device can hover at all. A touchscreen
 * laptop with a trackpad reports `hover: hover` with `any-pointer: coarse`: its hover-revealed
 * controls ARE reachable, so it should keep the lean desktop chrome. Its separate problem — a finger
 * tap latching the hover state — is fixed in `useDesktopHoverPlay` by looking at the real
 * `pointerType`, which is a question about input rather than capability.
 *
 * One shared MediaQueryList rather than one per caller: this is a device fact, identical for
 * everyone, and the library grid calls the hook once per card.
 */
const query =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MEDIA_QUERIES.noHover)
    : null;

function subscribe(onChange: () => void): () => void {
  query?.addEventListener('change', onChange);
  return () => query?.removeEventListener('change', onChange);
}

// Asking the NEGATIVE question and inverting, so every way of not getting an answer lands on "can
// hover" — no matchMedia at all (SSR, jsdom), or a browser that has matchMedia but doesn't
// understand the `hover` feature and so matches neither form. That keeps such an environment on the
// hover behaviour it had before this hook existed, and matches how the `no-hover` SCSS mixin fails.
const getSnapshot = () => !(query?.matches ?? false);

/**
 * Whether the device can hover. `false` on a phone or tablet, and on a touchscreen with no mouse
 * or trackpad attached.
 *
 * Reactive, because this genuinely changes mid-session: plugging a mouse into an iPad, or
 * detaching a 2-in-1's keyboard, flips the query. Distinct from `useIsMobile`, which is about
 * viewport WIDTH — a hoverless device can be any size, which is the whole bug this exists for.
 */
export function useHasHover(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}
